import { useCallback, useMemo, useRef, useState } from 'react';
import CostCodeSelect from '../cost-code-select/cost-code-select';
import { IGetCostCodesArgs, useGetCostCodesQuery } from 'redux-store/api/selections';
import { IProjectCostCodeSelect } from './types';
import { ISelectController } from 'controllers/select-controller';
import { getCostCodeOptionLabel } from '../cost-code-select/utils';

const getDefaultCostCodesParams = (projectId: number): IGetCostCodesArgs => ({
  projectId,
  limit: 10,
  cursor: undefined,
  search: undefined,
});

export const ProjectCostCodeSelect = ({ projectId, ...rest }: IProjectCostCodeSelect) => {
  const isOpenRef = useRef(null);

  const [costCodesParams, setCostCodesParams] = useState<IGetCostCodesArgs>(
    getDefaultCostCodesParams(projectId)
  );

  const { data: costCodesData, isFetching: isCostCodesFetching } =
    useGetCostCodesQuery(costCodesParams);

  const costCodesOptions: ISelectController['options'] = useMemo(() => {
    if (!costCodesData?.items?.length) {
      return [];
    }

    return costCodesData.items.map(({ id, name: itemName, number, category }) => ({
      label: getCostCodeOptionLabel({
        categoryNumber: category.number,
        costCodeNumber: number,
        costCodeName: itemName,
      }),
      value: id,
    }));
  }, [costCodesData?.items?.length]);

  const onIntersection = useCallback(() => {
    if (costCodesData?.meta?.nextCursor && isOpenRef.current) {
      setCostCodesParams((prev) => ({
        ...prev,
        cursor: costCodesData.meta.nextCursor,
      }));
    }
  }, [costCodesData?.meta?.nextCursor]);

  const resetCostCodesParams = useCallback(() => {
    setCostCodesParams(getDefaultCostCodesParams(projectId));
    isOpenRef.current = false;
  }, [getDefaultCostCodesParams, projectId]);

  const onOpen = () => {
    isOpenRef.current = true;
  };

  return (
    <CostCodeSelect
      isCostCodesFetching={isCostCodesFetching}
      options={costCodesOptions}
      resetCostCodesParams={resetCostCodesParams}
      setCostCodesParams={setCostCodesParams}
      onIntersection={onIntersection}
      onOpen={onOpen}
      {...rest}
    />
  );
};
